import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../../home/Navbar";
import Loading from "../../overlay/Loading";
import UsePagination from "../../reuse/UsePagination";
import AdminModal from "../shared/AdminModal";
import AdminPageHeader from "../shared/AdminPageHeader";
import ConfirmDeleteModal from "../shared/ConfirmDeleteModal";
import {
  adminCancelButtonClass,
  adminPrimaryButtonClass,
} from "../shared/adminStyles";
import QuotationForm from "./QuotationForm";
import QuotationsTable from "./QuotationsTable";
import QuotationProductsModal from "./QuotationProductsModal";
import QuotationPrintModal from "./QuotationPrintModal";
import {
  buildQuotationValidationMessage,
  emptyQuotationForm,
  emptyQuotationItem,
  getItemAmount,
  PAGE_SIZE,
} from "./quotationConfig";
import { API_URL } from "../../../../env";

const mapRecordToForm = (record) => ({
  quotationNo: String(record.quotationNo ?? ""),
  billTitle: record.billTitle ?? "",
  companyName: record.companyName ?? "",
  date: record.date
    ? new Date(record.date).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10),
  items: record.items?.map((item) => ({
    description: item.description ?? "",
    qty: String(item.qty ?? ""),
    rate: String(item.rate ?? ""),
    unit: item.unit ?? "",
  })) ?? [{ ...emptyQuotationItem }],
});

const createEmptyForm = () => ({
  quotationNo: "",
  billTitle: "",
  companyName: "",
  date: new Date().toISOString().slice(0, 10),
  items: [{ ...emptyQuotationItem }],
});

const ManageQuotations = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [productsModal, setProductsModal] = useState(null);
  const [printModal, setPrintModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [form, setForm] = useState(emptyQuotationForm);

  const searchRef = useRef(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (modalOpen) setModalOpen(false);
        if (deleteModal) setDeleteModal(null);
        if (productsModal) setProductsModal(null);
        if (printModal) setPrintModal(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, deleteModal, productsModal, printModal]);

  // return focus to search when modal closes
  useEffect(() => {
    if (!modalOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [modalOpen]);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/quotations`, {
        withCredentials: true,
      });
      setRecords(res.data ?? []);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to load quotations.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const filteredRecords = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return records;
    return records.filter((record) =>
      [
        record.quotationNo,
        record.billTitle,
        record.companyName,
        ...(record.items ?? []).flatMap((item) => [
          item.description,
          item.unit,
        ]),
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(query),
      ),
    );
  }, [records, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedRecords = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    return filteredRecords.slice(startIndex, startIndex + PAGE_SIZE);
  }, [page, filteredRecords]);

  const openCreateModal = async () => {
    setEditingRecord(null);
    setForm(createEmptyForm());
    setModalOpen(true);

    try {
      const res = await axios.get(`${API_URL}/quotations/next-number`, {
        withCredentials: true,
      });
      setForm((prev) => ({
        ...prev,
        quotationNo: String(res.data?.nextQuotationNo ?? ""),
      }));
    } catch {
      toast.error("Failed to fetch the next quotation number.");
    }
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    setForm(mapRecordToForm(record));
    setModalOpen(true);
  };

  const setItemField = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item,
      ),
    }));
  };

  const addItemRow = (afterIndex = null) => {
    setForm((prev) => {
      if (
        afterIndex === null ||
        afterIndex < 0 ||
        afterIndex >= prev.items.length
      ) {
        return { ...prev, items: [...prev.items, { ...emptyQuotationItem }] };
      }
      return {
        ...prev,
        items: [
          ...prev.items.slice(0, afterIndex + 1),
          { ...emptyQuotationItem },
          ...prev.items.slice(afterIndex + 1),
        ],
      };
    });
  };

  const removeItemRow = (index) => {
    setForm((prev) => ({
      ...prev,
      items:
        prev.items.length === 1
          ? [{ ...emptyQuotationItem }]
          : prev.items.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleSave = async () => {
    const validationMessage = buildQuotationValidationMessage(form);
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    const payload = {
      quotationNo: Number(form.quotationNo),
      billTitle: form.billTitle.trim(),
      companyName: form.companyName.trim(),
      date: form.date,
      items: form.items.map((item) => ({
        description: item.description.trim(),
        qty: Number(item.qty),
        rate: Number(item.rate),
        unit: item.unit.trim(),
        amount: getItemAmount(item),
      })),
    };

    try {
      setSaving(true);
      if (editingRecord) {
        await axios.patch(
          `${API_URL}/quotations/${editingRecord._id}`,
          payload,
          { withCredentials: true },
        );
        toast.success("Quotation updated.");
      } else {
        await axios.post(`${API_URL}/quotations`, payload, {
          withCredentials: true,
        });
        toast.success("Quotation created.");
      }
      setModalOpen(false);
      setEditingRecord(null);
      fetchRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save record.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      setDeleting(true);
      await axios.delete(`${API_URL}/quotations/${deleteModal._id}`, {
        withCredentials: true,
      });
      toast.success("Quotation deleted.");
      setDeleteModal(null);
      fetchRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete record.");
    } finally {
      setDeleting(false);
    }
  };

  const handlePrint = (record) => {
    setPrintModal(record);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!search.trim()) {
        openCreateModal();
      }
    }
  };

  return (
    <>
      <Navbar />
      {loading && <Loading />}

      <div className="mx-auto max-w-7xl p-2 md:p-4">
        <AdminPageHeader
          title="Quotations"
          description="Create quotations, manage products and pricing, and download printable images."
          actionLabel="Add Quotation"
          onAction={openCreateModal}
        />

        <div className="mb-4 flex gap-2">
          <div className="relative flex-1">
            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                />
              </svg>
            </span>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by quotation no, bill title, company, product... (Enter on empty = Add Quotation)"
              className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-4 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          {search && (
            <button
              onClick={() => {
                setSearch("");
                searchRef.current?.focus();
              }}
              className="flex items-center gap-1 rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-500 transition-colors hover:bg-red-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              Clear
            </button>
          )}
        </div>

        <QuotationsTable
          records={paginatedRecords}
          search={search}
          onShowProducts={setProductsModal}
          onPrint={handlePrint}
          onEdit={openEditModal}
          onDelete={setDeleteModal}
        />

        <div className="mt-4">
          <UsePagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            disabled={loading}
          />
        </div>
      </div>

      {modalOpen ? (
        <AdminModal
          title={editingRecord ? "Edit Quotation" : "Add Quotation"}
          onClose={() => setModalOpen(false)}
          wide
        >
          <QuotationForm
            form={form}
            onQuotationNoChange={(value) =>
              setForm((prev) => ({ ...prev, quotationNo: value }))
            }
            onBillTitleChange={(value) =>
              setForm((prev) => ({ ...prev, billTitle: value }))
            }
            onDateChange={(value) =>
              setForm((prev) => ({ ...prev, date: value }))
            }
            onCompanyNameChange={(value) =>
              setForm((prev) => ({ ...prev, companyName: value }))
            }
            onAddItemRow={addItemRow}
            onSetItemField={setItemField}
            onRemoveItemRow={removeItemRow}
            onSubmit={handleSave}
          />

          <div className="mt-5 flex gap-2">
            <button
              onClick={() => setModalOpen(false)}
              className={adminCancelButtonClass}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={adminPrimaryButtonClass}
            >
              {saving
                ? "Saving..."
                : editingRecord
                  ? "Save Changes"
                  : "Add Quotation"}
            </button>
          </div>
        </AdminModal>
      ) : null}

      <ConfirmDeleteModal
        open={Boolean(deleteModal)}
        title="Delete Quotation"
        message={
          deleteModal
            ? "Delete this quotation? This cannot be undone."
            : ""
        }
        deleting={deleting}
        onCancel={() => setDeleteModal(null)}
        onConfirm={handleDelete}
      />

      <QuotationProductsModal
        record={productsModal}
        onClose={() => setProductsModal(null)}
      />

      <QuotationPrintModal
        record={printModal}
        onClose={() => setPrintModal(null)}
      />
    </>
  );
};

export default ManageQuotations;